export default class HeroBackground {
  constructor() {
    this.heroContainer = document.querySelector('.hero-container')
    this.heroOrangeOverlay = this.heroContainer ? this.heroContainer.querySelector('.hero-orange-overlay') : null
    if (!this.heroContainer || !this.heroOrangeOverlay) {
      return
    }

    this.scrollThreshold = 10
    this.isShrunk = false // Tracks if overlay is shrunk to hero
    this.ticking = false
    this.hideTimeout = null // To manage timeout for hiding overlay
    this.resizeObserver = null

    if (!this.heroContainer || !this.heroOrangeOverlay) {
      console.error('HeroBackground: Hero setup incomplete. Missing .hero-container or .hero-orange-overlay')
      return
    }

    console.log('HeroBackground: Initialized. Overlay and Hero found.')
    this.init()
  }

  init() {
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.updateInitialOverlayPosition = this.updateInitialOverlayPosition.bind(this)
    this.handleImagesLoaded = this.handleImagesLoaded.bind(this)

    // Ensure overlay is visible and positioned initially
    this.heroOrangeOverlay.classList.remove('truly-hidden')
    this.heroOrangeOverlay.classList.remove('shrunk-to-hero')

    // Set initial position immediately
    this.updateInitialOverlayPosition()

    // Wait for fonts to load before final positioning
    document.fonts.ready.then(() => {
      this.updateInitialOverlayPosition()
      this.addTransitions()
      this.checkOverlayState()
    })

    // Set up resize observer to watch for layout changes
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isShrunk) {
        this.updateInitialOverlayPosition()
      }
    })
    this.resizeObserver.observe(this.heroContainer)

    // Watch for image loading
    this.watchImages()

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })
    console.log('HeroBackground: Event listeners added.')
  }

  watchImages() {
    // Watch all images in the hero container
    const images = this.heroContainer.getElementsByTagName('img')
    for (const img of images) {
      if (img.complete) {
        this.handleImagesLoaded()
      }
      else {
        img.addEventListener('load', this.handleImagesLoaded)
      }
    }
  }

  handleImagesLoaded() {
    if (!this.isShrunk) {
      this.updateInitialOverlayPosition()
    }
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.checkOverlayState()
        this.ticking = false
      })
      this.ticking = true
    }
  }

  handleResize() {
    console.log('HeroBackground: Resize event triggered.')
    this.updateInitialOverlayPosition()
    this.checkOverlayState()
  }

  addTransitions() {
    if (!this.heroOrangeOverlay) return

    // Create variable timing and easing for more organic animation
    const baseDelay = 0
    const baseDuration = 1000

    // Add some randomness to timing (but keep it consistent per page load)
    const seed = window.location.pathname.length // Simple seed based on URL
    const random1 = (seed * 9301 + 49297) % 233280 / 233280
    const random2 = ((seed + 1) * 9301 + 49297) % 233280 / 233280
    const random3 = ((seed + 2) * 9301 + 49297) % 233280 / 233280
    const random4 = ((seed + 3) * 9301 + 49297) % 233280 / 233280

    // Variable durations for each property (800ms to 1400ms)
    const topDuration = baseDuration + (random1 * 400 - 200)
    const leftDuration = baseDuration + (random2 * 400 - 200)
    const rightDuration = baseDuration + (random3 * 400 - 200)
    const bottomDuration = baseDuration + (random4 * 400 - 200)

    // Variable delays (0 to 200ms)
    const topDelay = baseDelay + (random1 * 200)
    const leftDelay = baseDelay + (random2 * 200)
    const rightDelay = baseDelay + (random3 * 200)
    const bottomDelay = baseDelay + (random4 * 200)

    // Different easing curves for variety
    const easings = [
      'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // ease-out
      'cubic-bezier(0.55, 0.085, 0.68, 0.53)', // ease-in-out
      'cubic-bezier(0.25, 0.1, 0.25, 1)', // ease
      'cubic-bezier(0.165, 0.84, 0.44, 1)', // ease-out-quart
    ]

    const topEasing = easings[Math.floor(random1 * easings.length)]
    const leftEasing = easings[Math.floor(random2 * easings.length)]
    const rightEasing = easings[Math.floor(random3 * easings.length)]
    const bottomEasing = easings[Math.floor(random4 * easings.length)]

    // Create the variable transition
    const transitionValue = [
      `top ${topDuration}ms ${topEasing} ${topDelay}ms`,
      `left ${leftDuration}ms ${leftEasing} ${leftDelay}ms`,
      `right ${rightDuration}ms ${rightEasing} ${rightDelay}ms`,
      `bottom ${bottomDuration}ms ${bottomEasing} ${bottomDelay}ms`,
      `transform 1200ms cubic-bezier(0.68, -0.55, 0.265, 1.55) 100ms`,
      `visibility 0s linear ${Math.max(topDuration, leftDuration, rightDuration, bottomDuration) + 200}ms`,
    ].join(', ')

    this.heroOrangeOverlay.style.transition = transitionValue
    console.log('HeroBackground: Variable transitions added:', transitionValue)
  }

  updateInitialOverlayPosition() {
    if (!this.heroOrangeOverlay) return // Guard clause

    const heroRect = this.heroContainer.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const scrollY = window.scrollY

    const initialTop = -(heroRect.top + scrollY)
    const initialLeft = -heroRect.left
    const initialRight = -(viewportWidth - heroRect.right)
    const initialBottom = -(viewportHeight - (heroRect.bottom + scrollY))

    console.log('HeroBackground: Hero rect:', heroRect)
    console.log('HeroBackground: Viewport:', { width: viewportWidth, height: viewportHeight, scrollY })
    console.log('HeroBackground: Calculated positions:', { initialTop, initialLeft, initialRight, initialBottom })

    // Set CSS custom properties
    this.heroOrangeOverlay.style.setProperty('--initial-top', `${initialTop}px`)
    this.heroOrangeOverlay.style.setProperty('--initial-left', `${initialLeft}px`)
    this.heroOrangeOverlay.style.setProperty('--initial-right', `${initialRight}px`)
    this.heroOrangeOverlay.style.setProperty('--initial-bottom', `${initialBottom}px`)

    // Apply positions directly if not shrunk
    if (!this.isShrunk) {
      this.heroOrangeOverlay.style.top = `${initialTop}px`
      this.heroOrangeOverlay.style.left = `${initialLeft}px`
      this.heroOrangeOverlay.style.right = `${initialRight}px`
      this.heroOrangeOverlay.style.bottom = `${initialBottom}px`

      console.log('HeroBackground: Applied positions to overlay')
      console.log('HeroBackground: Overlay computed style:', {
        top: this.heroOrangeOverlay.style.top,
        left: this.heroOrangeOverlay.style.left,
        right: this.heroOrangeOverlay.style.right,
        bottom: this.heroOrangeOverlay.style.bottom,
        visibility: getComputedStyle(this.heroOrangeOverlay).visibility,
        zIndex: getComputedStyle(this.heroOrangeOverlay).zIndex,
      })
    }
  }

  checkOverlayState() {
    if (!this.heroOrangeOverlay) return
    const scrollPosition = window.scrollY
    // Calculate dynamic animation duration based on the longest transition
    const animationDuration = 1400 // Maximum possible duration + some buffer

    console.log(`HeroBackground: checkOverlayState - scrollY: ${scrollPosition}, isShrunk: ${this.isShrunk}, threshold: ${this.scrollThreshold}`)

    clearTimeout(this.hideTimeout)

    if (scrollPosition > this.scrollThreshold) {
      if (!this.isShrunk) {
        console.log('HeroBackground: Scroll DOWN - Adding .shrunk-to-hero, removing .truly-hidden')
        this.heroOrangeOverlay.classList.remove('truly-hidden')
        // Ensure direct styles are cleared so CSS classes take over for shrink animation
        this.heroOrangeOverlay.style.top = ''
        this.heroOrangeOverlay.style.left = ''
        this.heroOrangeOverlay.style.right = ''
        this.heroOrangeOverlay.style.bottom = ''
        this.heroOrangeOverlay.classList.add('shrunk-to-hero')
        this.isShrunk = true
        this.hideTimeout = setTimeout(() => {
          console.log('HeroBackground: Timeout - Adding .truly-hidden')
          this.heroOrangeOverlay.classList.add('truly-hidden')
        }, animationDuration)
      }
    }
    else {
      // At the top of the page - ensure overlay covers viewport (but don't animate back from shrunk state)
      if (!this.isShrunk) {
        console.log('HeroBackground: At top - Ensuring overlay covers viewport')
        this.heroOrangeOverlay.classList.remove('truly-hidden')
        this.heroOrangeOverlay.classList.remove('shrunk-to-hero')
        // Apply initial positions to cover viewport
        this.updateInitialOverlayPosition()
      }
      // If already shrunk, leave it shrunk (no scroll-up expansion)
    }
  }
}
